import * as React from "react";
import {useNavigate, useParams} from "react-router-dom";
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    Link,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import {toast} from "react-toastify";
import LoadingDialog from "../../components/loadingDialog";
import SingleSelect from "../../components/SingleSelect";
import {
    WEEKDAYS,
    campaignEstimate,
    campaignRepAdd,
    campaignRepRemove,
    campaignUpsert,
    fetchCampaign,
    fetchCampaignWhenStarts,
    fetchCompaniesActive,
    getPostEventById,
    halfHourTimes,
    qualityOptions,
    quarterHourTimes,
    searchBrokersOptions,
    searchPostEventOptions,
    searchSchoolsOptions,
    weekdayLabel,
} from "./campaignsService";

const oldUrl = process.env.REACT_APP_OLD_URL || "https://www2.degreenexus.com/admin";

const labelSx = {
    fontSize: 12.5,
    lineHeight: 1.2,
    py: 0.5,
    pr: 1,
    color: "text.secondary",
    display: "flex",
    alignItems: "center",
};
const fieldSx = {
    "& .MuiInputBase-root": {fontSize: 12.5, minHeight: 30},
    "& .MuiInputBase-input": {py: 0.5, px: 1},
    "& .MuiSelect-select": {py: 0.5, px: 1},
};
const multilineFieldSx = {
    ...fieldSx,
    "& textarea": {resize: "none", overflowY: "auto", fontSize: 12.5, lineHeight: 1.35},
};
const checkLabelSx = {mr: 0.75, ml: 0, my: 0, "& .MuiFormControlLabel-label": {fontSize: 12.5}};
const denseSelectSx = {minWidth: 100, ...fieldSx};
const singleSelectDenseSx = {
    "& .MuiButton-root": {
        minHeight: 30,
        height: 30,
        py: 0,
        px: 1,
        fontSize: 12.5,
        lineHeight: 1.2,
        "& .MuiSvgIcon-root": {fontSize: 18},
    },
};

type LtRow = {weekday: number; startTime: string; endTime: string};

function bitsFromMask(mask: number): number[] {
    return WEEKDAYS.map((w) => w.bit).filter((b) => (mask & b) > 0);
}

function displayEnd(t: string) {
    if (t === "23:59:59" || t === "23:59") {
        return "23:59:59";
    }
    return (t || "00:00").substring(0, 5);
}

export default function CampaignUpsertPage() {
    const {id: idParam} = useParams();
    const navigate = useNavigate();
    const isNew = !idParam || idParam === "new";
    const campaignId = isNew ? 0 : Number(idParam);

    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [companies, setCompanies] = React.useState<any[]>([]);
    const [whenStarts, setWhenStarts] = React.useState<any[]>([]);

    const [companyId, setCompanyId] = React.useState(1);
    const [school, setSchool] = React.useState<any>(undefined);
    const [broker, setBroker] = React.useState<any>(undefined);
    const [postEvent, setPostEvent] = React.useState<any>(undefined);
    const [postEventTypedId, setPostEventTypedId] = React.useState("");
    const [classes, setClasses] = React.useState(1);
    const [active, setActive] = React.useState(1);
    const [rnOnly, setRnOnly] = React.useState(false);
    const [leadQualityMin, setLeadQualityMin] = React.useState(100);
    const [leadQualityMax, setLeadQualityMax] = React.useState(100);
    const [startTime, setStartTime] = React.useState("00:00");
    const [endTime, setEndTime] = React.useState("23:59:59");
    const [hoursOldMin, setHoursOldMin] = React.useState(1);
    const [hoursOldMax, setHoursOldMax] = React.useState(6);
    const [weekdays, setWeekdays] = React.useState<number[]>(WEEKDAYS.map((w) => w.bit));
    const [whenStartIds, setWhenStartIds] = React.useState<number[]>([1, 2, 4, 8, 16]);
    const [ltWeekdaySel, setLtWeekdaySel] = React.useState<number[]>([]);
    const [ltStart, setLtStart] = React.useState("00:00");
    const [ltEnd, setLtEnd] = React.useState("23:59:59");
    const [ltHours, setLtHours] = React.useState<LtRow[]>([]);
    const [dailyCap, setDailyCap] = React.useState(0);
    const [oldDailyCap, setOldDailyCap] = React.useState(0);
    const [monthlyCap, setMonthlyCap] = React.useState(0);
    const [costPerLead, setCostPerLead] = React.useState(0);
    const [companyPostUrl, setCompanyPostUrl] = React.useState("");
    const [description, setDescription] = React.useState("");
    const [clientUrl, setClientUrl] = React.useState("");
    const [username, setUsername] = React.useState("");
    const [password, setPassword] = React.useState("");
    const [appId, setAppId] = React.useState("");
    const [apiUrl, setApiUrl] = React.useState("");
    const [postConfirm, setPostConfirm] = React.useState("");
    const [comment, setComment] = React.useState("");
    const [comments, setComments] = React.useState<any[]>([]);
    const [assignedReps, setAssignedReps] = React.useState<any[]>([]);
    const [availableReps, setAvailableReps] = React.useState<any[]>([]);
    const [addRepId, setAddRepId] = React.useState<number | "">("");
    const [createRepId, setCreateRepId] = React.useState(42);
    const [isManaged, setIsManaged] = React.useState(false);
    const [isPortalRedirect, setIsPortalRedirect] = React.useState(false);
    const [fulfilled, setFulfilled] = React.useState({yesterday: 0, today: 0, mtd: 0, lastMonth: 0});
    const [maxSold, setMaxSold] = React.useState(5);
    const [estimateResult, setEstimateResult] = React.useState("");

    const applyPostEvent = (pe: any) => {
        if (!pe) {
            return;
        }
        setPostEvent(pe);
        if (pe.schoolId) {
            setSchool({
                id: pe.schoolId,
                value: `${pe.schoolName || ""} (${pe.schoolId})`,
            });
        }
        if (pe.brokerId) {
            setBroker({
                id: pe.brokerId,
                value: `${pe.brokerName || ""} (${pe.brokerId})`,
            });
            setHoursOldMin(1);
            setHoursOldMax(6);
        }
    };

    const load = React.useCallback(async () => {
        setLoading(true);
        const [cos, ws] = await Promise.all([fetchCompaniesActive(), fetchCampaignWhenStarts()]);
        setCompanies(cos);
        setWhenStarts(ws);

        if (isNew) {
            setWhenStartIds(ws.length ? ws.map((w: any) => w.id) : [1, 2, 4, 8, 16]);
            setLoading(false);
            return;
        }

        const c = await fetchCampaign(campaignId);
        if (!c) {
            toast.error("Campaign not found");
            setLoading(false);
            return;
        }

        setCompanyId(c.companyId);
        setSchool({id: c.schoolId, value: `${c.schoolName} (${c.schoolId})`});
        setBroker(c.brokerId ? {id: c.brokerId, value: `${c.brokerName} (${c.brokerId})`} : undefined);
        setClasses(c.classes);
        setActive(c.active);
        setRnOnly(!!c.rnOnly);
        setLeadQualityMin(c.leadQualityMin);
        setLeadQualityMax(c.leadQualityMax);
        setStartTime((c.startTime || "00:00").substring(0, 5));
        setEndTime(displayEnd(c.endTime));
        setHoursOldMin(c.hoursOldMin);
        setHoursOldMax(c.hoursOldMax);
        setWeekdays(bitsFromMask(c.weekdays));
        setWhenStartIds(whenStartsFromMask(c.whenStartId, ws));
        setLtHours((c.ltHours || []).map((h: any) => ({
            weekday: h.weekday,
            startTime: (h.startTime || "").substring(0, 8),
            endTime: (h.endTime || "").substring(0, 8),
        })));
        setDailyCap(c.dailyCap);
        setOldDailyCap(c.dailyCap);
        setMonthlyCap(c.monthlyCap);
        setCostPerLead(c.costPerLead);
        setCompanyPostUrl(c.companyPostUrl || "");
        setDescription(c.description || "");
        setClientUrl(c.clientUrl || "");
        setUsername(c.username || "");
        setPassword(c.password || "");
        setAppId(c.appId || "");
        setApiUrl(c.apiUrl || "");
        setPostConfirm(c.postConfirm || "");
        setComments(c.comments || []);
        setAssignedReps(c.assignedReps || []);
        setAvailableReps(c.availableReps || []);
        setAddRepId(c.availableReps?.[0]?.id || "");
        setIsManaged(!!c.isManaged);
        setIsPortalRedirect(!!c.isPortalRedirect);
        setFulfilled({
            yesterday: c.fulfilledYesterday,
            today: c.fulfilledToday,
            mtd: c.fulfilledMtd,
            lastMonth: c.fulfilledLastMonth,
        });

        if (c.hpEventId) {
            const pe = await getPostEventById(c.hpEventId);
            if (pe) {
                setPostEvent(pe);
            } else {
                setPostEvent({id: c.hpEventId, value: `Post Event ${c.hpEventId}`});
            }
        }
        setLoading(false);
    }, [campaignId, isNew]);

    React.useEffect(() => {
        load();
    }, [load]);

    const onBrokerChange = (item: any) => {
        setBroker(item);
        setHoursOldMin(1);
        setHoursOldMax(6);
    };

    const onPostEventChange = (item: any) => {
        applyPostEvent(item);
    };

    const onTypedPostEvent = async () => {
        const n = Number(postEventTypedId);
        if (!n) {
            return;
        }
        const pe = await getPostEventById(n);
        if (pe) {
            applyPostEvent(pe);
        } else {
            toast.error("Post event not found");
        }
    };

    const toggleWeekday = (bit: number) => {
        setWeekdays((prev) => (prev.includes(bit) ? prev.filter((b) => b !== bit) : [...prev, bit]));
    };

    const toggleWhenStart = (id: number) => {
        setWhenStartIds((prev) => (prev.includes(id) ? prev.filter((b) => b !== id) : [...prev, id]));
    };

    const addLtRows = () => {
        if (!ltWeekdaySel.length) {
            return;
        }
        setLtHours((prev) => [
            ...prev,
            ...ltWeekdaySel.map((weekday) => ({
                weekday,
                startTime: ltStart.length === 5 ? `${ltStart}:00` : ltStart,
                endTime: ltEnd === "23:59:59" ? "23:59:59" : (ltEnd.length === 5 ? `${ltEnd}:00` : ltEnd),
            })),
        ]);
    };

    const removeLtRow = (idx: number) => {
        setLtHours((prev) => prev.filter((_, i) => i !== idx));
    };

    const runEstimate = async () => {
        const whenStart = whenStartIds.reduce((a, b) => a + b, 0);
        const result = await campaignEstimate({
            hpCampaignId: campaignId,
            leadQualityMin,
            leadQualityMax,
            hoursOldMin,
            hoursOldMax,
            whenStartId: whenStart,
            maxSold,
        });
        setEstimateResult(result);
    };

    const handleAddRep = async () => {
        if (!addRepId || isNew) {
            return;
        }
        setSaving(true);
        const updated = await campaignRepAdd(campaignId, Number(addRepId));
        if (updated) {
            setAssignedReps(updated.assignedReps || []);
            setAvailableReps(updated.availableReps || []);
            setAddRepId(updated.availableReps?.[0]?.id || "");
            toast.success("Rep added");
        }
        setSaving(false);
    };

    const handleRemoveRep = async (repId: number) => {
        if (!window.confirm("Are you sure you want to delete this rep?")) {
            return;
        }
        setSaving(true);
        const updated = await campaignRepRemove(campaignId, repId);
        if (updated) {
            setAssignedReps(updated.assignedReps || []);
            setAvailableReps(updated.availableReps || []);
            setAddRepId(updated.availableReps?.[0]?.id || "");
            toast.success("Rep removed");
        }
        setSaving(false);
    };

    const validate = () => {
        if (!broker?.id) {
            toast.error("Invalid broker, nothing selected.");
            return false;
        }
        if (!school?.id) {
            toast.error("Invalid school");
            return false;
        }
        if (!postEvent?.id) {
            toast.error("Invalid post event");
            return false;
        }
        if (leadQualityMin > leadQualityMax) {
            toast.error("Lead quality must have max quality >= min quality.");
            return false;
        }
        const startIdx = halfHourTimes().indexOf(startTime);
        const endTimes = halfHourTimes(true);
        const endIdx = endTimes.indexOf(endTime);
        if (startIdx >= 0 && endIdx >= 0 && startIdx > endIdx) {
            toast.error("Must have start time less than end time.");
            return false;
        }
        if (Number(dailyCap) !== 0 && !String(companyPostUrl || "").trim()) {
            toast.error("Unique Variable String must not be empty if daily cap is being set.");
            return false;
        }
        if (!whenStartIds.length) {
            toast.error("Select at least one When Start");
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validate()) {
            return;
        }
        setSaving(true);
        const result = await campaignUpsert({
            id: campaignId,
            companyId: Number(companyId),
            schoolId: Number(school.id),
            brokerId: Number(broker.id),
            classes: Number(classes),
            hpEventId: Number(postEvent.id),
            active: Number(active),
            rnOnly,
            leadQualityMin: Number(leadQualityMin),
            leadQualityMax: Number(leadQualityMax),
            startTime,
            endTime,
            hoursOldMin: Number(hoursOldMin),
            hoursOldMax: Number(hoursOldMax),
            weekdays,
            whenStartIds,
            dailyCap: Number(dailyCap),
            oldDailyCap: Number(oldDailyCap),
            monthlyCap: Number(monthlyCap),
            costPerLead: Number(costPerLead),
            companyPostUrl: companyPostUrl || "",
            description: description || "",
            clientUrl: clientUrl || "",
            username: username || "",
            password: password || "",
            appId: appId || "",
            apiUrl: apiUrl || "",
            postConfirm: postConfirm || "",
            comment: comment || "",
            ltHours,
            createRepId: isNew ? Number(createRepId) : null,
        });
        setSaving(false);
        if (result) {
            toast.success(isNew ? "Campaign created" : "Campaign updated");
            if (Number(result.active) === 0) {
                window.location.href = `${oldUrl}/../new/ui/index.php#/campaigns`;
                return;
            }
            navigate(`/campaigns/${result.id}`, {replace: true});
            if (!isNew) {
                setComment("");
                await load();
            }
        }
    };

    if (loading) {
        return <LoadingDialog/>;
    }

    const startTimes = halfHourTimes();
    const endTimes = halfHourTimes(true);
    const ltStarts = quarterHourTimes();
    const ltEnds = quarterHourTimes(true);
    const qualities = qualityOptions();
    const managedDisabled = isManaged;

    return (
        <Box sx={{p: 1.5, maxWidth: 960}}>
            {(saving) && <LoadingDialog/>}
            <Typography variant="subtitle1" sx={{fontWeight: 700, mb: 1}}>
                {isNew ? "New Campaign" : "Update Campaign"}
            </Typography>

            <Grid container spacing={0.5} alignItems="center" columnSpacing={1}>
                <Grid item xs={12} sm={2} sx={labelSx}>Company / School / Broker</Grid>
                <Grid item xs={12} sm={10} sx={{display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap"}}>
                    <FormControl size="small" sx={{width: 88, minWidth: 72, ...fieldSx}}>
                        <Select value={companyId} onChange={(e) => setCompanyId(Number(e.target.value))}>
                            {companies.map((c) => (
                                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box sx={{flex: 1, minWidth: 180, ...singleSelectDenseSx}}>
                        <SingleSelect searchFunction={searchSchoolsOptions} selectedItem={school} setSelectedItem={setSchool} placeholder="School"/>
                    </Box>
                    <Box sx={{flex: 1, minWidth: 180, ...singleSelectDenseSx}}>
                        <SingleSelect searchFunction={searchBrokersOptions} selectedItem={broker} setSelectedItem={onBrokerChange} placeholder="Broker"/>
                    </Box>
                </Grid>

                <Grid item xs={12} sm={2} sx={labelSx}>Classes</Grid>
                <Grid item xs={12} sm={4}>
                    <FormControl size="small" sx={denseSelectSx}>
                        <Select value={classes} onChange={(e) => setClasses(Number(e.target.value))}>
                            <MenuItem value={1}>Online</MenuItem>
                            <MenuItem value={0}>Campus</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={2} sx={labelSx}>Active</Grid>
                <Grid item xs={12} sm={4}>
                    <FormControl size="small" sx={denseSelectSx}>
                        <Select value={active} onChange={(e) => setActive(Number(e.target.value))}>
                            <MenuItem value={1}>Yes</MenuItem>
                            {[2, 3, 4, 5, 6, 7, 8].map((v) => (
                                <MenuItem key={v} value={v}>Do not Use</MenuItem>
                            ))}
                            <MenuItem value={0}>No</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={2} sx={labelSx}>Post Event</Grid>
                <Grid item xs={12} sm={10} sx={{display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap"}}>
                    <Box sx={{flex: 1, minWidth: 280, maxWidth: 480, ...singleSelectDenseSx}}>
                        <SingleSelect searchFunction={searchPostEventOptions} selectedItem={postEvent} setSelectedItem={onPostEventChange} placeholder="Select post event"/>
                    </Box>
                    <TextField size="small" placeholder="ID" value={postEventTypedId} onChange={(e) => setPostEventTypedId(e.target.value)} sx={{width: 90, ...fieldSx}}/>
                    <Button size="small" variant="outlined" onClick={onTypedPostEvent} sx={{py: 0.25}}>Apply</Button>
                </Grid>

                <Grid item xs={12} sm={2} sx={labelSx}>RN ONLY</Grid>
                <Grid item xs={12} sm={4}>
                    <FormControl size="small" sx={denseSelectSx}>
                        <Select value={rnOnly ? 1 : 0} onChange={(e) => setRnOnly(Number(e.target.value) === 1)}>
                            <MenuItem value={0}>No</MenuItem>
                            <MenuItem value={1}>Yes</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={2} sx={labelSx}>Lead Quality</Grid>
                <Grid item xs={12} sm={4} sx={{display: "flex", gap: 0.75, alignItems: "center"}}>
                    <FormControl size="small" sx={{width: 72, ...fieldSx}}>
                        <Select value={leadQualityMin} onChange={(e) => setLeadQualityMin(Number(e.target.value))}>
                            {qualities.map((q) => <MenuItem key={q} value={q}>{q}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Typography sx={{fontSize: 12}}>–</Typography>
                    <FormControl size="small" sx={{width: 72, ...fieldSx}}>
                        <Select value={leadQualityMax} onChange={(e) => setLeadQualityMax(Number(e.target.value))}>
                            {qualities.map((q) => <MenuItem key={q} value={q}>{q}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={2} sx={labelSx}>Time Active</Grid>
                <Grid item xs={12} sm={10} sx={{display: "flex", gap: 0.75, alignItems: "center"}}>
                    <FormControl size="small" sx={denseSelectSx}>
                        <Select value={startTime} onChange={(e) => setStartTime(e.target.value)}>
                            {startTimes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Typography sx={{fontSize: 12}}>to</Typography>
                    <FormControl size="small" sx={denseSelectSx}>
                        <Select value={endTime} onChange={(e) => setEndTime(e.target.value)}>
                            {endTimes.map((t) => (
                                <MenuItem key={t} value={t}>{t === "23:59:59" ? "23:59" : t}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={2} sx={labelSx}>Hours Old</Grid>
                <Grid item xs={12} sm={10} sx={{display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap"}}>
                    <TextField size="small" type="number" inputProps={{step: 0.01, min: 0}} value={hoursOldMin}
                               onChange={(e) => setHoursOldMin(Number(e.target.value))} sx={{width: 80, minWidth: 80, ...fieldSx}}/>
                    <Typography sx={{fontSize: 12}}>to</Typography>
                    <TextField size="small" type="number" inputProps={{step: 0.01, min: 0}} value={hoursOldMax}
                               onChange={(e) => setHoursOldMax(Number(e.target.value))} sx={{width: 80, minWidth: 80, ...fieldSx}}/>
                    {!isNew && (
                        <>
                            <Button size="small" variant="outlined" onClick={runEstimate} sx={{py: 0.25}}>Estimate</Button>
                            <Typography sx={{fontSize: 12}}>Max Sold:</Typography>
                            <TextField size="small" type="number" value={maxSold} onChange={(e) => setMaxSold(Number(e.target.value))} sx={{width: 64, ...fieldSx}}/>
                            <Typography sx={{fontSize: 12}}>{estimateResult}</Typography>
                        </>
                    )}
                </Grid>

                <Grid item xs={12} sm={2} sx={labelSx}>Days Active</Grid>
                <Grid item xs={12} sm={10}>
                    {WEEKDAYS.map((w) => (
                        <FormControlLabel
                            key={w.bit}
                            sx={checkLabelSx}
                            control={
                                <Checkbox
                                    size="small"
                                    disabled={managedDisabled}
                                    checked={weekdays.includes(w.bit)}
                                    onChange={() => toggleWeekday(w.bit)}
                                    sx={{p: 0.35}}
                                />
                            }
                            label={w.label}
                        />
                    ))}
                </Grid>

                <Grid item xs={12} sm={2} sx={{...labelSx, alignItems: "flex-start", pt: 1}}>Daytime (PST)</Grid>
                <Grid item xs={12} sm={10}>
                    <Table size="small" sx={{maxWidth: 460, "& td, & th": {border: "none", py: 0.35, px: 0.75, verticalAlign: "middle", fontSize: 12.5}}}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{fontWeight: 600, width: 130}}>Weekday</TableCell>
                                <TableCell sx={{fontWeight: 600, width: 100}}>Start</TableCell>
                                <TableCell sx={{fontWeight: 600, width: 100}}>End</TableCell>
                                <TableCell sx={{fontWeight: 600, width: 48}}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell>
                                    <FormControl size="small" fullWidth sx={fieldSx}>
                                        <Select
                                            multiple
                                            disabled={managedDisabled}
                                            value={ltWeekdaySel}
                                            onChange={(e) => setLtWeekdaySel(e.target.value as number[])}
                                            renderValue={(selected) => (selected as number[]).map(weekdayLabel).join(", ") || "—"}
                                            displayEmpty
                                        >
                                            {WEEKDAYS.map((w) => (
                                                <MenuItem key={w.bit} value={w.bit}>{w.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                                <TableCell>
                                    <FormControl size="small" fullWidth sx={fieldSx}>
                                        <Select disabled={managedDisabled} value={ltStart} onChange={(e) => setLtStart(e.target.value)}>
                                            {ltStarts.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                                <TableCell>
                                    <FormControl size="small" fullWidth sx={fieldSx}>
                                        <Select disabled={managedDisabled} value={ltEnd} onChange={(e) => setLtEnd(e.target.value)}>
                                            {ltEnds.map((t) => (
                                                <MenuItem key={t} value={t}>{t === "23:59:59" ? "23:59" : t}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                                <TableCell>
                                    <Button size="small" variant="outlined" disabled={managedDisabled} onClick={addLtRows} sx={{minWidth: 32, px: 0.5}}>+</Button>
                                </TableCell>
                            </TableRow>
                            {ltHours.map((row, idx) => (
                                <TableRow key={`${row.weekday}-${idx}`} sx={{"& td": {borderTop: "1px solid #eee"}}}>
                                    <TableCell>{weekdayLabel(row.weekday)}</TableCell>
                                    <TableCell>{row.startTime}</TableCell>
                                    <TableCell>{row.endTime === "23:59:59" ? "23:59" : row.endTime}</TableCell>
                                    <TableCell>
                                        <Button size="small" variant="outlined" color="inherit" disabled={managedDisabled} onClick={() => removeLtRow(idx)} sx={{minWidth: 32, px: 0.5}}>-</Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Grid>

                <Grid item xs={12} sm={2} sx={labelSx}>When Start</Grid>
                <Grid item xs={12} sm={10}>
                    {whenStarts.map((w: any) => (
                        <FormControlLabel
                            key={w.id}
                            sx={checkLabelSx}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={whenStartIds.includes(w.id)}
                                    onChange={() => toggleWhenStart(w.id)}
                                    sx={{p: 0.35}}
                                />
                            }
                            label={w.name}
                        />
                    ))}
                </Grid>

                <Grid item xs={12} sm={2} sx={labelSx}>Rep Owner(s)</Grid>
                <Grid item xs={12} sm={10}>
                    {isNew ? (
                        <FormControl size="small" sx={{minWidth: 260, ...fieldSx}}>
                            <Select value={createRepId} onChange={(e) => setCreateRepId(Number(e.target.value))}>
                                <MenuItem value={42}>jenay@banneredgemedia.com</MenuItem>
                                <MenuItem value={51}>chris@firsthavenmedia.com</MenuItem>
                            </Select>
                        </FormControl>
                    ) : (
                        <Box sx={{display: "flex", flexDirection: "column", gap: 0.5}}>
                            <Box sx={{display: "flex", gap: 0.75, alignItems: "center"}}>
                                <FormControl size="small" sx={{minWidth: 260, ...fieldSx}}>
                                    <Select value={addRepId} onChange={(e) => setAddRepId(e.target.value as number)}>
                                        {availableReps.map((r: any) => (
                                            <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Button size="small" variant="outlined" onClick={handleAddRep} sx={{py: 0.25}}>ADD</Button>
                            </Box>
                            {assignedReps.map((r: any) => (
                                <Box key={r.id} sx={{fontSize: 12.5}}>
                                    {r.name}{" "}
                                    <Button size="small" onClick={() => handleRemoveRep(r.id)} sx={{py: 0, minWidth: 0}}>DEL</Button>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Grid>

                <Grid item xs={12} sm={2} sx={{...labelSx, alignItems: "flex-start", pt: 0.75}}>Post Confirm</Grid>
                <Grid item xs={12} sm={10}>
                    <TextField multiline rows={3} fullWidth value={postConfirm} onChange={(e) => setPostConfirm(e.target.value)} sx={multilineFieldSx}/>
                </Grid>

                <Grid item xs={12} sm={2} sx={{...labelSx, alignItems: "flex-start", pt: 0.75}}>New Comment</Grid>
                <Grid item xs={12} sm={10}>
                    <TextField multiline rows={2} fullWidth value={comment} onChange={(e) => setComment(e.target.value)} sx={multilineFieldSx}/>
                </Grid>

                <Grid item xs={12} sm={2} sx={{...labelSx, alignItems: "flex-start", pt: 0.75}}>Comments</Grid>
                <Grid item xs={12} sm={10}>
                    <Box sx={{width: "100%", height: 72, overflow: "auto", border: "1px solid #bbb", backgroundColor: "#eee", p: 0.75, fontSize: 12}}>
                        {comments.map((c, i) => (
                            <Box key={i} sx={{mb: 0.5}}>
                                <b>{c.date}->{c.userName}</b>
                                <br/>
                                {String(c.comment || "").split("\n").map((line: string, j: number) => (
                                    <React.Fragment key={j}>{line}<br/></React.Fragment>
                                ))}
                            </Box>
                        ))}
                    </Box>
                </Grid>

                <Grid item xs={12} sm={2} sx={labelSx}>Caps / CPL</Grid>
                <Grid item xs={12} sm={10} sx={{display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap"}}>
                    <Typography sx={{fontSize: 11, color: "text.secondary"}}>D</Typography>
                    <TextField
                        size="small"
                        value={dailyCap}
                        disabled={isPortalRedirect}
                        onChange={(e) => setDailyCap(Number(e.target.value))}
                        sx={{width: 88, minWidth: 88, ...fieldSx}}
                    />
                    <Typography sx={{fontSize: 11, color: "text.secondary"}}>M</Typography>
                    <TextField
                        size="small"
                        value={monthlyCap}
                        disabled={isPortalRedirect}
                        onChange={(e) => setMonthlyCap(Number(e.target.value))}
                        sx={{width: 88, minWidth: 88, ...fieldSx}}
                    />
                    <Typography sx={{fontSize: 11, color: "text.secondary"}}>CPL</Typography>
                    <TextField size="small" value={costPerLead} onChange={(e) => setCostPerLead(Number(e.target.value))} sx={{width: 88, minWidth: 88, ...fieldSx}}/>
                    <Typography sx={{fontSize: 11, color: "text.secondary"}}>
                        Y:{fulfilled.yesterday} T:{fulfilled.today} MTD:{fulfilled.mtd} LM:{fulfilled.lastMonth}
                    </Typography>
                </Grid>

                <Grid item xs={12} sm={2} sx={labelSx}>
                    Unique Var
                    <Typography component="span" sx={{fontSize: 10, color: "primary.main", ml: 0.5}}>?pub=X</Typography>
                </Grid>
                <Grid item xs={12} sm={10}>
                    <TextField size="small" fullWidth value={companyPostUrl} onChange={(e) => setCompanyPostUrl(e.target.value)} sx={fieldSx}/>
                </Grid>

                <Grid item xs={12} sm={2} sx={labelSx}>
                    {apiUrl ? <Link href={apiUrl} target="_blank" sx={{fontSize: 12.5}}>API Url</Link> : "API Url"}
                </Grid>
                <Grid item xs={12} sm={10}>
                    <TextField size="small" fullWidth value={apiUrl} onChange={(e) => setApiUrl(e.target.value)} sx={fieldSx}/>
                </Grid>

                <Grid item xs={12} sm={2} sx={{...labelSx, alignItems: "flex-start", pt: 0.75}}>Description</Grid>
                <Grid item xs={12} sm={10}>
                    <TextField
                        multiline
                        rows={2}
                        fullWidth
                        value={description}
                        inputProps={{maxLength: 250}}
                        onChange={(e) => setDescription(e.target.value)}
                        sx={multilineFieldSx}
                        helperText={`${250 - (description || "").length} left`}
                        FormHelperTextProps={{sx: {mt: 0.25, fontSize: 11}}}
                    />
                </Grid>

                <Grid item xs={12} sm={2} sx={labelSx}>Client / Creds</Grid>
                <Grid item xs={12} sm={10} sx={{display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center"}}>
                    <TextField size="small" placeholder="Client Url" value={clientUrl} onChange={(e) => setClientUrl(e.target.value)} sx={{flex: 1, minWidth: 200, ...fieldSx}}/>
                    <TextField size="small" placeholder="Username" value={username} onChange={(e) => setUsername(e.target.value)} sx={{width: 140, ...fieldSx}}/>
                    <TextField size="small" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} sx={{width: 140, ...fieldSx}}/>
                </Grid>

                <Grid item xs={12} sm={2} sx={labelSx}>App ID</Grid>
                <Grid item xs={12} sm={10}>
                    <TextField size="small" value={appId} onChange={(e) => setAppId(e.target.value)} sx={{width: 280, ...fieldSx}}/>
                </Grid>

                <Grid item xs={12} sx={{textAlign: "center", mt: 1.25, mb: 1}}>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        size="small"
                        sx={{minWidth: 120, height: 36, fontSize: 14, backgroundColor: "#0580cc"}}
                    >
                        {isNew ? "SUBMIT" : "UPDATE"}
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
}

function whenStartsFromMask(mask: number, options: any[]): number[] {
    if (!options?.length) {
        return WEEKDAYS.map((w) => w.bit).filter((b) => (mask & b) > 0);
    }
    return options.map((o) => o.id).filter((id: number) => (mask & id) > 0);
}
